import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ApiService } from '@/services/apiService'
import type { LegalEntityFormData, PhysicalPersonFormData } from '../../../shared/types'

export type FormType = 'legal_entity' | 'physical_person' | null

export interface FormState {
  currentStep: number
  maxStep: number
  formType: FormType | null
  isSubmitting: boolean
  lastActivity: number
  formData: LegalEntityFormData | PhysicalPersonFormData | null
  sessionId: string | null
  sessionExpiresAt: string | null
}

export const useFormStore = defineStore('form', () => {
  const state = ref<FormState>({
    currentStep: 1,
    maxStep: 1,
    formType: null,
    isSubmitting: false,
    lastActivity: Date.now(),
    formData: null,
    sessionId: null,
    sessionExpiresAt: null
  })

  const apiService = new ApiService()

  // Computed
  const canGoNext = computed(() => !isLastStep.value)
  const canGoPrevious = computed(() => state.value.currentStep > 1)
  const isFirstStep = computed(() => state.value.currentStep === 1)
  const isLastStep = computed(() => {
    if (state.value.formType === 'legal_entity') return state.value.currentStep === 6
    if (state.value.formType === 'physical_person') return state.value.currentStep === 7
    return false
  })

  // Actions
  async function setFormType(type: FormType) {
    state.value.formType = type
    state.value.currentStep = 1
    state.value.maxStep = 1
    
    // Initialize fresh form data (completely ephemeral - no persistence)
    if (type) {
      if (type === 'legal_entity') {
        state.value.formData = {
          formType: 'legal_entity',
          step1: {} as any,
          step2: {} as any,
          step3: {} as any,
          step4: {} as any,
          step5: {} as any,
          step6: {} as any
        } as LegalEntityFormData
      } else if (type === 'physical_person') {
        state.value.formData = {
          formType: 'physical_person',
          step1: {} as any,
          step2: {} as any,
          step3: {} as any,
          step4: {} as any,
          step5: {} as any,
          step6: {} as any,
          step7: {} as any
        } as PhysicalPersonFormData
      }
    }
    
    updateActivity()
  }

  function nextStep() {
    console.log('nextStep called:', {
      canGoNext: canGoNext.value,
      currentStep: state.value.currentStep,
      maxStep: state.value.maxStep,
      isLastStep: isLastStep.value
    })
    
    if (canGoNext.value) {
      state.value.currentStep++
      if (state.value.currentStep > state.value.maxStep) {
        state.value.maxStep = state.value.currentStep
      }
      updateActivity()
      console.log('Step advanced to:', state.value.currentStep)
    } else {
      console.log('Cannot go next - blocked by canGoNext')
    }
  }

  function previousStep() {
    if (canGoPrevious.value) {
      state.value.currentStep--
      updateActivity()
    }
  }

  function goToStep(step: number) {
    if (step >= 1 && step <= state.value.maxStep) {
      state.value.currentStep = step
      updateActivity()
    }
  }

  function updateActivity() {
    state.value.lastActivity = Date.now()
  }

  function setSubmitting(submitting: boolean) {
    state.value.isSubmitting = submitting
  }

  // No saving logic - forms are completely ephemeral

  // This method is no longer needed - files are uploaded immediately to backend

  // Upload file directly to backend and return response
  async function uploadFileToBackend(file: File, fieldName: string): Promise<{ fileId: string } | null> {
    if (!state.value.formType) return null
    try {
      return await apiService.uploadFile(file, fieldName, state.value.formType, state.value.sessionId || undefined)
    } catch (error) {
      console.error('Failed to upload file to backend:', error)
      throw error
    }
  }

  // Submit form via AJAX
  async function submitForm() {
    if (!state.value.formData || !state.value.formType) {
      throw new Error('No form data to submit')
    }

    state.value.isSubmitting = true
    try {
      let response
      if (state.value.formType === 'legal_entity') {
        response = await apiService.submitLegalEntityForm(state.value.formData as LegalEntityFormData, state.value.sessionId || undefined)
      } else {
        response = await apiService.submitPhysicalPersonForm(state.value.formData as PhysicalPersonFormData, state.value.sessionId || undefined)
      }

      if (response.success) {
        // Clear form data on successful submission
        state.value.formData = null
        return response
      } else {
        throw new Error(response.message)
      }
    } finally {
      state.value.isSubmitting = false
    }
  }

  // No saved data - always return false
  function hasFormData(_formType: FormType): boolean {
    return false
  }

  // Reset form state (no localStorage to clear)
  function reset() {
    state.value.currentStep = 1
    state.value.maxStep = 1
    state.value.formType = null
    state.value.isSubmitting = false
    state.value.formData = null
    state.value.sessionId = null
    state.value.sessionExpiresAt = null
    updateActivity()
  }

  // Create session after Turnstile verification
  async function createSession(turnstileToken: string): Promise<void> {
    try {
      const response = await apiService.createSession(turnstileToken)
      state.value.sessionId = response.sessionId
      state.value.sessionExpiresAt = response.expiresAt
      updateActivity()
    } catch (error) {
      console.error('Failed to create session:', error)
      throw error
    }
  }

  // Check if session is valid (not expired)
  function isSessionValid(): boolean {
    if (!state.value.sessionId || !state.value.sessionExpiresAt) {
      return false
    }
    return new Date() < new Date(state.value.sessionExpiresAt)
  }

  // No drafts - always return false
  function checkForDraft(_formType: FormType): boolean {
    return false
  }

  // Update form data (for step components) - no saving
  function updateFormData(updates: any) {
    if (state.value.formData) {
      state.value.formData = { ...state.value.formData, ...updates } as LegalEntityFormData | PhysicalPersonFormData
      updateActivity()
    }
  }

  return {
    state,
    canGoNext,
    canGoPrevious,
    isFirstStep,
    isLastStep,
    setFormType,
    nextStep,
    previousStep,
    goToStep,
    updateActivity,
    setSubmitting,
    createSession,
    isSessionValid,
    uploadFileToBackend,
    submitForm,
    hasFormData,
    reset,
    checkForDraft,
    updateFormData
  }
})
